<template>
    <infinite-scroll
        :load-more="loadMore"
        :can-load-more="props.collection.total.value > items.length"
    >
        <slot v-if="items.length > 0" :bindings="{ items: items }" />
        <template v-if="items.length <= 0">
            <slot v-if="loading" name="loader">
                <flex-container :padding-y="Size.xl">
                    <spinner-loader />
                </flex-container>
            </slot>
            <slot v-if="!loading" name="placeholder" />
        </template>
        <template
            v-if="items.length > 0"
            #endMessage
        >
            <slot name="endMessage">
                <flex-container
                    justify-content="center"
                    :padding-top="Size.sm"
                    :padding-bottom="Size.md"
                >
                    <text-decorator
                        :size="FontSize.xs"
                        color="gray-500"
                        bold
                    >
                        Je hebt het einde bereikt
                    </text-decorator>
                </flex-container>
            </slot>
        </template>
    </infinite-scroll>
</template>

<script setup lang="ts" generic="T extends Resource">
    import { FontSize, InfiniteScroll, Size } from '@keyvue/one-ui';
    import type { ReactiveCollection } from '@/services/keyvue-api/composables/useClient';
    import type { Resource } from '@keyvue/one-api-client';
    import { onMounted, type Ref, ref, watch } from 'vue';

    const props = withDefaults(defineProps<{
        collection: ReactiveCollection<T>,
        itemsPerLoad?: number,
        params?: object,
    }>(), {
        itemsPerLoad: 25,
        params: () => ({}),
    });

    const reloading = ref(false);
    const loading = ref(false);
    const page = ref(1);
    const items: Ref<T[]> = ref([]);
    const lastLoadedParams = ref();

    onMounted(async () => {
        lastLoadedParams.value = props.params;
        await load();
    });

    async function load() {
        loading.value = true;

        await props.collection.load({
            ...props.params,
            pagination: true,
            itemsPerPage: props.itemsPerLoad,
            page: page.value,
        });

        if (props.collection.count.value > 0) {
            props.collection.data.value.forEach((resource: T) => {
                items.value.push(resource);
            });
        }

        loading.value = false;
        reloading.value = false;
    }

    async function loadMore(): Promise<boolean> {
        if (props.collection.count.value > 0) {
            page.value += 1;
            await load();
            return true;
        }

        return false;
    }

    async function reloadOnParamChange() {
        reloading.value = true;
        page.value = 1;
        items.value = [];
        await load();
        lastLoadedParams.value = props.params;
    }

    /**
     * The infinite scrollable collection doesnt necessarily remount when a filter changes
     * So we keep track whenever the params change so we know when to reload the entire collection
     */
    watch(() => props.params, async () => {
        if (JSON.stringify(props.params) === JSON.stringify(lastLoadedParams.value)) {
            return;
        }

        await reloadOnParamChange();
    });

    defineExpose({
        items,
    });
</script>
